import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, makeStyles, Theme, Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { Dialog, Grid, Stack } from "@mui/material";
import {
  GetNutrientInfoQuery,
  NutrientInfoWithSource,
  NutrientSource,
  NutrientsResponse,
  useGetNutrientInfoLazyQuery,
} from "../../api/smartmelts-api/graphql/generated";
import { useTranslation } from "react-i18next";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { MenuCategoryDictionary } from "../../models/categories";
import { ChefHat } from "../../assets/icons/chef-hat";
import { Wrench } from "../../assets/icons/wrench";
import { CircleColorIcon } from "../../assets/icons/circle-color-icon";
import { Cancel } from "../../assets/icons/cancel";
import { PulseLoader } from "react-spinners";
import { ApolloError } from "@apollo/client";
import { HeadingText } from "../text/heading-text";
import { ContentWithIconButton } from "./components/content-with-icon-button";
import { HeaderWithIconButton } from "./components/header-with-icon-button";
import { SnackbarSeverity, useSnackbar } from "../snackbar/snackbar-context";
import { CONTENT_MAX_WIDTH } from "../../theme/theme";
import { NutrientSourceBlock } from "./components/nutrient-source-block";
import { ReferenceText } from "./components/reference-text";
import { BulletText } from "./components/bullet-text";
import _ from "lodash";

interface INutrientModalStyleProps {
  color: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  topContainer: {
    padding: theme.spacing(10),
    paddingTop: theme.spacing(15),
  },
  bottomContainerWrapper: {
    backgroundColor: theme.palette.background.default,
  },
  bottomContainer: {
    padding: theme.spacing(10),
  },
  cardTitles: {
    fontSize: 18,
    fontWeight: 700,
  },
  cardBottomTitle: {
    fontSize: 14,
    fontWeight: 700,
  },
  cardBottomBody: {
    fontSize: 14,
    fontWeight: 300,
  },

  chefHat: {
    paddingRight: theme.spacing(2),
  },
  close: {
    position: "absolute",
    top: 0,
    left: 40,
    width: 24,
    height: 24,
    padding: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    cursor: "pointer",
    zIndex: 100,
    transition: "background-color 0.3s ease",
    backgroundColor: ({ color }: INutrientModalStyleProps) => color,
  },
  dialogPaper: {
    maxWidth: CONTENT_MAX_WIDTH,
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  dialogPaperRoot: {
    [theme.breakpoints.up("md")]: {
      minWidth: 750,
    },
  },
  sourcesContainer: {
    paddingTop: theme.spacing(4),
  },
  nudgeIn: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  sourceContainer: {
    paddingTop: theme.spacing(4),
  },
  padTop: {
    paddingTop: theme.spacing(6),
  },
}));

interface INutrientModalProps {
  open: boolean;
  onClose: () => void;
  nutrient: NutrientsResponse;
}

export const NutrientModal: FunctionComponent<INutrientModalProps> = (props) => {
  const { activeCategory } = useContext(NutritionContext);
  const { color } = MenuCategoryDictionary[activeCategory];
  const theme = useTheme();
  const classes = useStyles({ color: color ? color : theme.palette.primary.main });
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { nutrient, open, onClose } = props;
  const { name } = nutrient;
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [nutrientInfo, setNutrientInfo] = useState<NutrientInfoWithSource | null>(null);

  const [getNutrientInfo, { loading }] = useGetNutrientInfoLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: (data: GetNutrientInfoQuery) => {
      setNutrientInfo(data.getNutrientInfo || null);
    },
    onError: (error: ApolloError) => {
      showSnackbar(t("snackbar.get_nutrient_info_error"), SnackbarSeverity.ERROR, true);
    },
  });

  useEffect(() => {
    if (open) {
      getNutrientInfo({
        variables: {
          name: nutrient.name,
        },
      });
    }
  }, [getNutrientInfo, nutrient, open]);

  const references: string[] = useMemo(() => {
    return nutrientInfo?.references ?? [];
  }, [nutrientInfo]);

  const nutrientFunctions = useMemo(() => {
    if (open) {
      return t(`nutrient.values.${nutrient.name}.functions`);
    }
    return "";
  }, [open, t, nutrient]);

  const functionsToShow: string[] = useMemo(() => {
    if (typeof nutrientFunctions === "object") {
      return Object.values(nutrientFunctions).map((value) => {
        return value as string;
      });
    }
    return [nutrientFunctions];
  }, [nutrientFunctions]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      PaperProps={{
        className: classes.dialogPaper,
        classes: {
          root: classes.dialogPaperRoot,
        },
        style: {
          borderRadius: 40,
          height: isSmallScreen ? undefined : "90vh",
        },
      }}
      open={open}
      onClose={handleClose}
      fullWidth
    >
      <Box className={classes.close} onClick={handleClose}>
        <Cancel width="24px" height="24px" style={{ cursor: "pointer" }} />
      </Box>
      {loading && (
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={0}
          style={{ minHeight: "90vh" }}
        >
          <Grid item xs={3}>
            <PulseLoader size={32} />
          </Grid>
        </Grid>
      )}
      {!loading && !nutrientInfo && (
        <Grid container className={classes.topContainer}>
          <Grid item container xs={12} direction="row" alignItems="center">
            <ChefHat className={classes.chefHat} />
            <Grid item>
              <HeadingText>{t("nutrient_modal.food_sources")}</HeadingText>
            </Grid>
          </Grid>
          <Grid item container xs={12} spacing={3} direction="row" className={classes.sourcesContainer}>
            {nutrient.sources.map((source: NutrientSource, idx: number) => (
              <Grid item xs={6} key={`${source.name.substring(0, 5)}-${idx}`}>
                <NutrientSourceBlock source={source} index={idx + 1} />
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      {!loading && nutrientInfo && (
        <Grid container direction="column" alignItems="flex-start">
          <Grid item container className={classes.topContainer} spacing={1}>
            <Grid item xs={12}>
              <HeadingText>{t(`nutrient.values.${name}.name`)}</HeadingText>
            </Grid>
            <Grid item xs={12}>
              <ContentWithIconButton text={nutrientInfo.info} />
            </Grid>
            <Grid item xs={12}>
              <Grid container className={classes.padTop}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Wrench className={classes.cardTitles} />
                  <HeaderWithIconButton text={nutrientInfo.functionsHeader} />
                </Stack>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.nudgeIn}>
                {_.uniq(functionsToShow).map((translatedString, idx) => (
                  <BulletText key={idx}>
                    <span>{translatedString}</span>
                  </BulletText>
                ))}
              </div>
            </Grid>

            <Grid item xs={12}>
              <Grid container className={classes.padTop}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ChefHat className={classes.cardTitles} />
                  <HeaderWithIconButton text={nutrientInfo.foodSourcesHeader} />
                </Stack>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container className={classes.nudgeIn}>
                {nutrientInfo.foodSources.map((source: NutrientSource, idx: number) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={`${source.name.substring(0, 5)}-${idx}`}
                    className={classes.sourceContainer}
                  >
                    <NutrientSourceBlock source={source} index={idx + 1} />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            {nutrientInfo.bioAvailability.length > 5 && (
              <>
                <Grid item xs={12}>
                  <Grid container className={classes.padTop}>
                    <HeaderWithIconButton text={nutrientInfo.bioAvailabilityHeader} />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <ContentWithIconButton text={nutrientInfo.bioAvailability} />
                </Grid>
              </>
            )}
            {nutrientInfo.foodPreparation.length > 5 && (
              <>
                <Grid item xs={12}>
                  <Grid container className={classes.padTop}>
                    <HeaderWithIconButton text={nutrientInfo.foodPreparationHeader} />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <ContentWithIconButton text={nutrientInfo.foodPreparation} />
                </Grid>
              </>
            )}
          </Grid>

          <Grid item container className={classes.bottomContainerWrapper}>
            <Grid item container spacing={2} className={classes.bottomContainer}>
              <Grid item>
                <Typography className={classes.cardBottomTitle}>{t("nutrient_modal.references")}:</Typography>
              </Grid>

              {references.map((reference: string, idx: number) => (
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="flex-start"
                  wrap="nowrap"
                  spacing={1}
                  key={`${reference.substring(0, 5)}-${idx}`}
                >
                  <Grid item id={`${idx + 1}`}>
                    <Typography className={classes.cardBottomBody}>
                      <CircleColorIcon color={theme.palette.primary.main}>{`${idx + 1}`}</CircleColorIcon>
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <ReferenceText reference={reference} />
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Dialog>
  );
};
